import React, { FC, useState } from "react";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { Col, Row, Tag } from "antd";

import {
  ExpandedTable,
  FileField,
  IButton,
  ConfirmModal,
  ApprovalFormModal,
  DraggableModal,
  ITooltip,
} from "../../../ui-kit";
import {
  ButtonEditIco,
  HistoryIco,
  PlusIco,
  TrashIco,
} from "../../../assets/customIcons";
import DictionaryGroupForm from "./DictionaryGroup";
import CustomDictionaryForm from "./CustomDictionary";
import DictionaryItemForm from "./DictionaryItem";
import DictionariesStore from "../../../store/dictionary";
import CustomDictionaryEditForm from "./CustomDictionaryEdit";
import DictionaryGroupEditForm from "./DictionaryGroupEdit";
import DictionaryItemEditForm from "./DictionaryItemEdit";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import AttributesHistory from "./AttributesHistory";
import DictionaryVersion from "./DictionaryVersion";

import styles from "../styles.module.scss";
import Constants from "../../../constants/Common";

const CustomDictionaryChildren: FC = observer((values) => {
  const {
    titleIcons: { trash, history: historyIcon, edit },
  } = Constants;
  const [isShowAddItemForm, setIsShowAddItemForm] = useState(false);
  const [isShowAddGroupForm, setIsShowAddGroupForm] = useState(false);
  const [isShowAddDictForm, setIsShowAddDictForm] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState({
    show: false,
    id: null,
    title: "",
  });
  const handleShowHistoryModal = () =>
    setShowHistoryModal((data) => ({ ...data, show: !data.show }));

  const onShowHistory = (e, { dictId, title }) => {
    e.stopPropagation();

    setShowHistoryModal({ show: true, id: dictId, title });
  };

  const columns = [
    {
      title: "Наименование",
      key: "title",
      render: (text, record, index) =>
        record.isGroup ? record.groupTitle : record.title,
      width: "40%",
      className: "align-left",
    },
    {
      title: "Тип",
      key: "type",
      render: (text, record, index) =>
        record.isGroup ? "Группа" : "Справочник",
      sortable: true,
      width: "5%",
      className: "align-left",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      width: "40%",
      className: "align-left",
    },
    {
      title: "Статус согласования",
      key: "status",
      render: (_, record) => {
        const params = {
          color: "blue",
          children: record?.agreementStatus?.entityStateName,
          onClick: (e) => {
            e.stopPropagation();
            setApprovalDictId(record.dictId);
          },
        };
        if (record?.agreementStatus?.entityState == 3) {
          params.color = "red";
        }
        if (record?.agreementStatus?.entityState == 2) {
          params.color = "green";
        }
        if (record?.agreementStatus?.entityState == 1) {
          params.color = "yellow";
        }
        return record.isGroup ? <></> : <Tag {...params}></Tag>;
      },
    },
    {
      title: "Статус",
      key: "status",
      render: (_, record) => {
        const params = {
          color: "green",
          children: "Активен",
        };
        if (record?.isBlocked) {
          params.color = "yellow";
          params.children = "Заблокирован";
        }
        return record?.isGroup ? <></> : <Tag {...params}></Tag>;
      },
    },
    {
      title: "Количество элементов",
      key: "itemsCount",
      width: "5%",
      className: "align-left",
      render: (text, record, index) =>
        !record.isGroup
          ? `${record.itemsCount}${
              record.itemsFilteredCount != record.itemsCount
                ? " (" + record.itemsFilteredCount + ")"
                : ""
            }`
          : "",
    },
    {
      title: " ",
      key: "edit",
      render: (text, record, index) => {
        const onClick = (e) => {
          let keys = toJS(values?.record?.editableKeys.keys);
          const isExpanded = keys.includes(record.id);
          if (!isExpanded && values?.record?.keys.keys.includes(record.id)) {
            e.stopPropagation();
          }
          if (isExpanded) {
            keys = keys.filter((item) => item !== record.id);
          } else {
            keys.push(record.id);
          }
          values?.record?.editableKeys.setKeys(keys);
        };
        return (
          <ITooltip title={edit} placement="right">
            <ButtonEditIco onClick={onClick} />
          </ITooltip>
        );
      },
      className: "delete-cell",
    },
    {
      title: " ",
      key: "history",
      fixed: "right",
      render: (text, record, index) =>
        !record.isGroup ? (
          <ITooltip title={historyIcon} placement="right">
            <HistoryIco onClick={(e) => onShowHistory(e, record)} />
          </ITooltip>
        ) : null,
      className: "delete-cell",
    },
    {
      title: " ",
      key: "delete",
      render: (text, record, index) => (
        <ITooltip title={trash} placement="right">
          <TrashIco onClick={(e) => onDeleteRow(e, record)} />
        </ITooltip>
      ),
      className: "delete-cell",
    },
  ];
  const columnsItems = [
    {
      title: "Значение",
      key: "dictionaryValue",
      dataIndex: "dictionaryValue",
      width: "90%",
      className: "align-left",
    },
    {
      title: " ",
      key: "edit",
      render: (text, record, index) => {
        const onClick = () => {
          let keys = toJS(values?.record?.keys.keys);
          const isExpanded = keys.includes(record.id);
          if (isExpanded) {
            keys = keys.filter((item) => item !== record.id);
          } else {
            keys.push(record.id);
          }
          values?.record?.keys.setKeys(keys);
        };
        return <ButtonEditIco onClick={onClick} />;
      },
      className: "delete-cell",
    },
    {
      title: " ",
      key: "delete",
      render: (text, record, index) => (
        <TrashIco onClick={(e) => deleteItem(e, record)} />
      ),
      className: "delete-cell",
    },
  ];
  const hasShortDictionaryValue = values?.record.items.some((item) => {
    return !!item.shortDictionaryValue;
  });

  if (hasShortDictionaryValue || values?.record?.dictId === 900202) {
    columnsItems.unshift({
      title: "Код",
      key: "shortDictionaryValue",
      dataIndex: "shortDictionaryValue",
      width: "5%",
      className: "align-left",
    });
  }

  const onFillDictItems = (files) => {
    const formData = new FormData();
    formData.append("file", files[0]);
    debugger;
    DictionariesStore.fillDictItems(values?.record?.dictId, formData);
  };

  const deleteData = (rec) => {
    if (rec.isGroup) DictionariesStore.deleteGroup(rec.groupId);
    else DictionariesStore.deleteDict(rec.dictId);
  };

  const {
    confirmModalData: confirmModalDataItems,
    toggleConfirmModal: toggleConfirmModalItems,
    closeConfirmModal: closeConfirmModalItems,
    confirmDelete: confirmDeleteItems,
  } = useDeleteConfirm(DictionariesStore.deleteDictItem);

  const {
    confirmModalData,
    toggleConfirmModal,
    closeConfirmModal,
    confirmDelete,
  } = useDeleteConfirm(deleteData);

  const onDeleteRow = (e, rec) => {
    e.stopPropagation();
    toggleConfirmModal({ show: true, id: rec });
  };

  const deleteItem = (e, rec) => {
    e.stopPropagation();
    toggleConfirmModalItems({ show: true, id: rec });
  };

  const [approvalDictId, setApprovalDictId] = useState(null);

  return values?.record?.isGroup ? (
    <div style={{ marginLeft: `${(values?.record?.level + 1) * 20}px` }}>
      <Row gutter={[20, 20]}>
        <Col span={24}>
          <ExpandedTable
            key={values?.record?.editableKeys.keys.join("")}
            rowKeys={values?.record?.keys.keys}
            setRowKeys={values?.record?.keys.setKeys}
            columns={columns}
            data={values?.record?.groups}
            className={styles.UsersTable}
            rowExpandable={() => true}
            expandedRowRender={(record) => {
              if (
                record?.isGroup &&
                values?.record?.editableKeys.keys.includes(record.id)
              ) {
                return (
                  <>
                    <a
                      onClick={(e) =>
                        values?.record?.editableKeys.remove(record.id)
                      }
                    >
                      Вернуться к списку
                    </a>
                    {React.cloneElement(<DictionaryGroupEditForm />, {
                      record,
                    })}
                  </>
                );
              }
              if (
                !record?.isGroup &&
                values?.record?.editableKeys.keys.includes(record.id)
              ) {
                return (
                  <>
                    <a
                      onClick={(e) =>
                        values?.record?.editableKeys.remove(record.id)
                      }
                    >
                      Вернуться к списку
                    </a>
                    {React.cloneElement(<CustomDictionaryEditForm />, {
                      record,
                    })}
                  </>
                );
              }
              return React.cloneElement(<CustomDictionaryChildren />, {
                record,
              });
            }}
          />
        </Col>
        <Col span={5}>
          <IButton
            size="medium"
            onClick={(e) => setIsShowAddDictForm(!isShowAddDictForm)}
          >
            <PlusIco /> Добавить справочник
          </IButton>
        </Col>
        <Col span={4}>
          <IButton
            size="medium"
            onClick={(e) => setIsShowAddGroupForm(!isShowAddGroupForm)}
          >
            <PlusIco /> Добавить группу
          </IButton>
        </Col>
        {isShowAddGroupForm && (
          <Col span={24}>
            <DictionaryGroupForm parentId={values?.record?.groupId} />
          </Col>
        )}
        {isShowAddDictForm && (
          <Col span={24}>
            <CustomDictionaryForm groupId={values?.record?.groupId} />
          </Col>
        )}
      </Row>
      <ConfirmModal
        message={`Вы действительно хотите удалить ${
          confirmModalData.id?.isGroup ? "группу" : "справочник"
        } "${
          confirmModalData.id?.isGroup
            ? confirmModalData.id?.groupTitle
            : confirmModalData.id?.title
        }"?`}
        visible={confirmModalData.show}
        onClose={closeConfirmModal}
        onConfirm={confirmDelete}
      />
      {approvalDictId && (
        <ApprovalFormModal
          entityId={approvalDictId}
          entityName="Справочники"
          onClose={() => setApprovalDictId(null)}
        />
      )}
      <DraggableModal
        visible={showHistoryModal.show}
        title={`История изменений`}
        footer={null}
        onCancel={handleShowHistoryModal}
      >
        <AttributesHistory dictionaryId={showHistoryModal.id} />
        <DictionaryVersion
          dictionaryId={showHistoryModal.id}
          dictionaryName={showHistoryModal.title}
        />
      </DraggableModal>
    </div>
  ) : (
    <Row gutter={[20, 20]}>
      <Col span={24}>
        {values?.record?.items.length > 0 && (
          <ExpandedTable
            rowKeys={values?.record?.keys.keys}
            setRowKeys={values?.record?.keys.setKeys}
            expandRowByClick={false}
            className={styles.lastChildTable}
            columns={columnsItems}
            data={values?.record?.items}
            //rowExpandable={(record) => !record?.isDeleted}
          >
            <DictionaryItemEditForm
              hasShortDictionaryValue={hasShortDictionaryValue}
            />
          </ExpandedTable>
        )}
        {values?.record?.items.length == 0 && <div>Нет значений</div>}
        {values?.record?.items.length == 0 && (
          <FileField
            onChange={onFillDictItems}
            label="Загрузить справочник из Excel"
          />
        )}
      </Col>
      <Col span={4}>
        <IButton
          size="medium"
          disabled={values?.record?.isDeleted}
          onClick={(e) => setIsShowAddItemForm(!isShowAddItemForm)}
        >
          <PlusIco /> Добавить значение
        </IButton>
      </Col>
      {isShowAddItemForm && (
        <Col span={24}>
          <DictionaryItemForm
            hasShortDictionaryValue={hasShortDictionaryValue}
            dictId={values?.record?.dictId}
          />
        </Col>
      )}
      <ConfirmModal
        visible={confirmModalDataItems.show}
        onClose={closeConfirmModalItems}
        onConfirm={confirmDeleteItems}
      />
    </Row>
  );
});

export default CustomDictionaryChildren;
